/**
 * Button component with variant and size support.
 *
 * A versatile button component built on Radix UI Slot for composition flexibility.
 * Supports multiple visual styles and sizes for different use cases.
 *
 * @example
 * ```tsx
 * // Default button
 * <Button>Click me</Button>
 *
 * // With variant
 * <Button variant="destructive">Delete</Button>
 * <Button variant="outline">Cancel</Button>
 * <Button variant="ghost">Ghost button</Button>
 * <Button variant="link">Link button</Button>
 * <Button variant="emerald">Success Action</Button>
 * <Button variant="purple">Creative Action</Button>
 * <Button variant="gold">Premium Action</Button>
 * <Button variant="pink">Featured Action</Button>
 *
 * // With size
 * <Button size="sm">Small</Button>
 * <Button size="lg">Large</Button>
 * <Button size="icon"><Icon /></Button>
 *
 * // As child (composition)
 * <Button asChild>
 *   <Link href="/">Navigate</Link>
 * </Button>
 * ```
 */
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { createMotionComponent, MOTION_PRESETS } from '@/lib/cva-motion';

/**
 * CVA variants for Button component.
 *
 * Variants:
 * - default: Primary button with primary background
 * - destructive: Destructive action button (red)
 * - outline: Outlined button with border
 * - secondary: Secondary button with secondary background
 * - ghost: Transparent button with hover effect
 * - link: Link-style button with underline
 * - emerald: Emerald green button
 * - purple: Purple button
 * - gold: Gold button
 * - pink: Pink button
 *
 * Sizes:
 * - default: Standard size (h-9)
 * - sm: Small size (h-8, compact padding)
 * - lg: Large size (h-10/11, increased padding)
 * - icon: Square icon button (h-9 w-9)
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md body-small font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        emerald: 'bg-emerald text-emerald-foreground hover:bg-emerald/80',
        purple: 'bg-purple text-purple-foreground hover:bg-purple/80',
        gold: 'bg-gold text-gold-foreground hover:bg-gold/80',
        pink: 'bg-pink text-pink-foreground hover:bg-pink/80',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 sm:h-11 px-4 sm:px-6',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/**
 * Props for the Button component.
 *
 * Extends standard HTML button attributes and adds variant, size, and asChild props.
 *
 * @property variant - Visual style variant (default, destructive, outline, secondary, ghost, link, emerald, purple, gold, pink)
 * @property size - Size variant (default, sm, lg, icon)
 * @property asChild - When true, renders as child component using Radix Slot
 */
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

/**
 * Button component with variant and size support.
 *
 * Features:
 * - Type-safe variants using CVA
 * - Composition support via Radix Slot (asChild prop)
 * - Accessible keyboard navigation
 * - Focus visible states
 * - Disabled state handling
 *
 * @param props - Button props including variant, size, asChild, and standard HTML button attributes
 * @returns A styled button element or composed component
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

/**
 * Motion-enhanced Button component with automatic animations.
 *
 * Provides the same API as Button but with built-in motion animations:
 * - Subtle scale on hover
 * - Scale down on tap
 * - Glow effect on focus
 * - Smooth initial mount animation
 *
 * @example
 * ```tsx
 * <MotionButton variant="emerald" size="lg">
 *   Animated Button
 * </MotionButton>
 * ```
 */
const MotionButton = createMotionComponent(buttonVariants, MOTION_PRESETS.button);

MotionButton.displayName = 'MotionButton';

export { Button, buttonVariants, MotionButton };
